import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    authApi,
    type LoginPayload,
    type PublicUser,
    type RegisterPayload,
} from "@/features/auth/api";
import { isAdminRole } from "@/features/auth/roles";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
    user: PublicUser | null;
    status: AuthStatus;
    isAuthenticated: boolean;
    isAdmin: boolean;
    login: (payload: LoginPayload) => Promise<PublicUser>;
    loginWithOtp: (payload: { email: string; code: string; remember: boolean }) => Promise<PublicUser>;
    loginWithGoogle: (sessionId: string) => Promise<PublicUser>;
    register: (payload: RegisterPayload) => Promise<PublicUser>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
    updateProfile: (payload: { full_name: string; phone: string }) => Promise<PublicUser>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<PublicUser | null>(null);
    const [status, setStatus] = useState<AuthStatus>("loading");

    const refresh = useCallback(async () => {
        try {
            let s = await authApi.session();
            // Access cookie may be expired even when a valid refresh cookie
            // is still around (Remember Me). Try one refresh before giving up.
            if (!s.authenticated) {
                try {
                    await authApi.refreshSession();
                    s = await authApi.session();
                } catch {
                    /* refresh not usable — treat as unauthenticated */
                }
            }
            if (s.authenticated && s.user) {
                setUser(s.user);
                setStatus("authenticated");
            } else {
                setUser(null);
                setStatus("unauthenticated");
            }
        } catch {
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);

    useEffect(() => {
        // Returning from Google OAuth: GoogleAuthCallback exchanges the session first.
        if (window.location.hash?.includes("session_id=")) {
            setStatus("unauthenticated");
            return;
        }
        refresh();
    }, [refresh]);

    const login = useCallback(async (payload: LoginPayload) => {
        const u = await authApi.login(payload);
        setUser(u);
        setStatus("authenticated");
        // Best-effort FCM push registration — no-op when unconfigured.
        try {
            const { registerPushToken } = await import("@/features/push/firebase");
            const { api } = await import("@/features/auth/api");
            void registerPushToken(api);
        } catch {
            /* push registration is best-effort */
        }
        return u;
    }, []);

    const loginWithOtp = useCallback(
        async (payload: { email: string; code: string; remember: boolean }) => {
            const u = await authApi.verifyLoginOtp(payload);
            setUser(u);
            setStatus("authenticated");
            return u;
        },
        [],
    );

    const loginWithGoogle = useCallback(async (sessionId: string) => {
        const u = await authApi.googleExchange(sessionId);
        setUser(u);
        setStatus("authenticated");
        return u;
    }, []);

    const register = useCallback(async (payload: RegisterPayload) => {
        const u = await authApi.register(payload);
        setUser(u);
        setStatus("authenticated");
        return u;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } finally {
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);

    const updateProfile = useCallback(
        async (payload: { full_name: string; phone: string }) => {
            const u = await authApi.updateProfile(payload);
            setUser(u);
            return u;
        },
        [],
    );

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            status,
            isAuthenticated: status === "authenticated" && !!user,
            isAdmin: isAdminRole(user?.role),
            login,
            loginWithOtp,
            loginWithGoogle,
            register,
            logout,
            refresh,
            updateProfile,
        }),
        [user, status, login, loginWithOtp, loginWithGoogle, register, logout, refresh, updateProfile],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx;
}
